import { Form as AntForm, Form, Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { useContractProfileLoadQuery } from "src/app/features/ContractProfile/services/server/ContractProfileApi";
import type { ContractProfileEntity } from "src/app/features/ContractProfile/types/ContractProfileEntity";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification, showWarningNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import { deepClone, isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";
import { useContractPhysicalSaveMutation } from "../../services/server/ContractApi";
import type { ContractEntity } from "../../types/ContractEntity";
import type { ContractAppProfileEntity } from "../../types/subTypes/ContractAppProfileEntity";

export const ContractPhysicalCreateForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("contract-physical");

    const [formData, setFormData] = useState<ContractEntity>({} as ContractEntity);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useContractPhysicalSaveMutation();

    // HANDLE ON CHANGE
    const onChangeCallback = useCallback(
        (newData: ContractEntity) => {
            setFormData({
                ...newData,
                appProfileListIdCheckReadOnly: formData.appProfileListIdCheckReadOnly,
            });
        },
        [formData],
    );

    // HANDLE WATCH FIELDS
    const [form] = AntForm.useForm();
    const contractProfileId = Form.useWatch("contractProfileId", form);
    const [isNewContractProfileIdChanged, setIsNewContractProfileIdChanged] = useState<boolean>(false);

    const { data: contractProfileEntity } = useContractProfileLoadQuery(
        { entityId: contractProfileId },
        { skip: !contractProfileId || !isNewContractProfileIdChanged },
    );

    const updateWatchedFields = useCallback(() => {
        if (!contractProfileEntity || !setIsNewContractProfileIdChanged) return null;

        const newFormData = deepClone(formData);
        const contractProfile = contractProfileEntity;

        // HANDLE UPDATE FIELDS
        // These fields inherit Contract Profile values
        const updateFields = [
            "acquirerId",
            "appProfileListId",
            "receiptHeader",
            "merchantData",
            "receiptFooter",
            "messageForSuccessfulTransaction",
            "messageForFailedTransaction",
            "courtesyMessage",
        ];
        if (contractProfile) {
            updateFields?.forEach((fieldName: string) => {
                newFormData[fieldName] = contractProfile[fieldName as keyof ContractProfileEntity];
            });
        }

        // HANDLE VISIBILITY OF FIELDS FROM RO PROPERTY IN CONTRACT PROFILE
        // For each RO field name from Contract Profile object, the value is assigned to local state.
        // RO properties are used to control visibility of the fields in form.
        newFormData.appProfileListIdCheckReadOnly = contractProfile?.["applicationProfileListRO" as keyof ContractProfileEntity];

        return newFormData;
    }, [contractProfileEntity, formData]);

    useEffect(() => {
        if (!isNewContractProfileIdChanged || !contractProfileEntity) {
            return;
        }
        let newFormData = updateWatchedFields();
        if (newFormData && !isTheSameByDeepCompare(formData, newFormData)) {
            setIsNewContractProfileIdChanged(false);
            setFormData(newFormData);
            newFormData = null;
        }
    }, [contractProfileEntity, formData, isNewContractProfileIdChanged, updateWatchedFields]);

    useEffect(() => {
        if (contractProfileId) {
            setIsNewContractProfileIdChanged(true);
        }
    }, [contractProfileId]);

    // HANDLE ON SAVE
    const shouldSaveContractAppProfile = ({ acceptMode, routingId, merchantId }: ContractAppProfileEntity): boolean => {
        return !!acceptMode || (!acceptMode && !routingId && !merchantId);
    };

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            // a contractAppProfile (accepted card type) is only valid to send to the BE if it has acceptedMode defined, alternatively if it is empty it should be skipped
            if (formData.acceptedCardProfiles?.some((item) => !shouldSaveContractAppProfile(item))) {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("contract.error.contractAppProfiles"),
                        autoCloseDisabled: true,
                    }),
                );
                return;
            }
            // Only include contractAppProfile if it has acceptedMode defined
            formData.acceptedCardProfiles = formData.acceptedCardProfiles?.filter((item) => !!item.acceptMode);

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formSaveSuccess", {
                            id: result.data.id,
                            resource: "Contract",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.CONTRACTS}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<ContractEntity>
                    name="PhysicalContractCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                    customFormInstance={form}
                />
            )}
        </Spin>
    );
};
